import React, {CSSProperties, forwardRef, RefObject, useLayoutEffect, useRef, useState} from 'react';
import styled from 'styled-components';

import {Input, InputProps} from './input';

/*
 * Constants.
 */

const defaultWidthStyleProp: keyof CSSProperties = 'width';

/*
 * Props.
 */

export interface AutosizeInputProps extends InputProps {
  /** The style prop to apply the input width to. Defaults to 'width'. */
  widthStyleProp?: keyof CSSProperties;
}

/*
 * Style.
 */

const StyledInvisibleSpan = styled.span`
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
`;

const StyledMeasureSpan = styled.span`
  white-space: nowrap;
`;

/*
 * Component.
 */

export const AutosizeInput = forwardRef<Input, AutosizeInputProps>((props, ref) => {
  const measureRef = useRef<HTMLElement>(null);
  const [width, setWidth] = useState<number>();
  const widthStyleProp = props.widthStyleProp || defaultWidthStyleProp;

  useLayoutEffect(() => {
    const newWidth = tryMeasureWidth(measureRef);
    setWidth(newWidth);
  }, [props.value, setWidth]);

  return (
    <>
      {/* styled-components recommends using a style object for frequently changed styles. */}
      <Input {...props} style={{[widthStyleProp]: width}} ref={ref} />
      <StyledInvisibleSpan>
        <StyledMeasureSpan ref={measureRef} className={props.className} role="presentation">
          {props.value || 'x' /* use 1ex as the minimum width */}
        </StyledMeasureSpan>
      </StyledInvisibleSpan>
    </>
  );
});

/*
 * Helpers.
 */

function tryMeasureWidth(elementRef: RefObject<HTMLElement>): number | undefined {
  const element = elementRef.current;
  if (!element) {
    return undefined;
  }

  const rect = element.getBoundingClientRect();
  return Math.ceil(rect.width);
}
